import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { blogCategories, blogPosts } from '../data/blogPosts';

const POSTS_PER_PAGE = 12;

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredPosts = useMemo(() => {
    if (activeCategory === 'All') {
      return blogPosts;
    }

    return blogPosts.filter((post) => post.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const pageStart = (currentPage - 1) * POSTS_PER_PAGE;
  const pageEnd = pageStart + POSTS_PER_PAGE;
  const paginatedPosts = filteredPosts.slice(pageStart, pageEnd);

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="calculator-page fade-in blog-page">
      <SEO
        title="Finance Blog & Money Tips"
        description={`${blogPosts.length} practical personal finance blog posts on budgeting, investing, tax planning, loans, retirement, and business money management in India.`}
        keywords="finance blog india, personal finance tips india, money management blog, budgeting tips, investing guide india, sip planning, retirement planning india, tax planning articles, loan planning tips, business finance guide, event fund planning"
        canonicalPath="/blog"
      />

      <div className="blog-header">
        <h1>Finance Blog</h1>
        <p>Learn money skills with {blogPosts.length} practical posts for everyday financial decisions.</p>
      </div>

      <div className="blog-toolbar">
        <span className="blog-count">
          {filteredPosts.length} posts · Page {currentPage} of {totalPages}
        </span>
        <div className="blog-categories">
          {blogCategories.map((category) => (
            <button
              key={category}
              type="button"
              className={`blog-category-chip${activeCategory === category ? ' active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="blog-grid">
        {paginatedPosts.map((post) => (
          <article key={post.id} className="blog-card">
            <div className="blog-card-meta">
              <span className="blog-card-category">{post.category}</span>
              <span>{formatDate(post.publishedAt)}</span>
              <span>{post.readMinutes} min read</span>
            </div>
            <h2 className="blog-card-title">
              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
            </h2>
            <p className="blog-card-excerpt">{post.excerpt}</p>
            <Link to={`/blog/${post.slug}`} className="blog-read-link">
              Read article
            </Link>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="blog-pagination" role="navigation" aria-label="Blog pages">
          <button
            type="button"
            className="blog-page-btn"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="blog-page-number-wrap">
            {pageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={`blog-page-btn blog-page-number${currentPage === pageNumber ? ' active' : ''}`}
                onClick={() => setCurrentPage(pageNumber)}
                aria-current={currentPage === pageNumber ? 'page' : undefined}
              >
                {pageNumber}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="blog-page-btn"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Blog;
